import { Location } from '../../location.model';
import { TimeEntryBreakDto } from '../../shifts/shift-breaks.dto';
import { RateInfo } from '../../shift-need.model';

export interface UnifiedShift {
  /**
   * Unique identifier of the current lifecycle entity.
   */
  id: string;

  /**
   * Represents the most advanced lifecycle stage currently active.
   */
  lifecycleStage:
    | 'need'
    | 'application'
    | 'request'
    | 'confirmed'
    | 'completed'
    | 'cancelled';

  /**
   * Shared shift information across all lifecycle entities.
   */
  shift: UnifiedShiftCoreDto;

  /**
   * Facility-related information.
   */
  facility: UnifiedFacilityDto;

  /**
   * Contractor information when applicable.
   * Present for applications, requests, and confirmed shifts.
   */
  contractor?: UnifiedContractorDto;

  /**
   * Contractor-driven application workflow data.
   */
  application?: UnifiedApplicationDto;

  /**
   * Facility-driven request/invitation workflow data.
   */
  request?: UnifiedRequestDto;

  /**
   * Confirmed assignment data.
   */
  assignment?: UnifiedAssignmentDto;

  /**
   * Payment and compensation information.
   */
  payment?: UnifiedPaymentDto;

  /**
   * Scheduling and recurrence configuration.
   */
  scheduling?: UnifiedSchedulingDto;

  /**
   * Internal metadata and auditing information.
   */
  metadata?: UnifiedMetadataDto;
}

export interface UnifiedShiftCoreDto {
  shiftNeedId?: string;

  shiftDate: string;

  startTime: string;

  endTime: string;

  estimatedHours?: number;

  shiftType: string | number;

  shiftTypeDisplay?: string;

  scopeOfPractice: string | number;

  scopeOfPracticeDisplay?: string;

  facilitySubtype: {
    id: number;
    name: string;
  };

  description?: string;

  specialInstructions?: string;

  status?: string;

  statusDisplay?: string;
}

export interface UnifiedFacilityDto {
  id: string;

  name: string;

  fullAddress?: string;

  location: Location;

  distanceMiles?: number;

  timezone?: string;
}

export interface UnifiedContractorDto {
  id: string;

  name: string;

  profilePictureUrl?: string;
}

export interface UnifiedApplicationDto {
  id: string;

  status: string;

  message?: string;

  requestedAt: string;

  respondedAt?: string;

  responseMessage?: string;

  negotiationActive: boolean;

  latestCounterOfferRate?: number;

  latestCounterOfferBy?: string;

  latestCounterOfferAt?: string;

  latestCounterOfferMessage?: string;

  initiatedBy?: 'facility' | 'contractor';

  expiresAt?: string;

  shiftNeedIsFull?: boolean;
}

export interface UnifiedRequestDto {
  id: string;

  status: string;

  requestedAt: string;

  respondedAt?: string;

  responseMessage?: string;

  initiatedBy?: 'facility' | 'contractor';
}

export interface UnifiedAssignmentDto {
  confirmedAt?: string;

  confirmedByName?: string;

  breaks?: TimeEntryBreakDto[];

  isEditable?: boolean;

  isAutoClockOut?: boolean;
}

export interface UnifiedPaymentDto {
  estimatedPayment?: number;

  shiftNeedHourlyRate?: number;

  payPeriodType?: string;

  paymentScheduleDisplay?: string;

  rates?: RateInfo;
}

export interface UnifiedSchedulingDto {
  isRecurring?: boolean;

  recurringPatternId?: number;

  shiftIds?: string[];

  breakDurationMinutes?: number;
}

export interface UnifiedMetadataDto {
  createdAt?: string;

  updatedAt?: string;

  expiresAt?: string;

  createdBy?: string;

  createdByName?: string;

  postedByName?: string;
}
